/**
 * Bilder in Beiträgen: öffentlich wie bisher oder privat (#5235).
 *
 * Privat heißt: Das Bild gehört im Storage dem Menschen, der es hochlädt,
 * und ist ohne Signatur nicht abrufbar. Damit es im Beitrag erscheint,
 * wird es an den Beitrag gebunden. Wer den Beitrag lesen darf, bekommt es
 * dann über kurzlebige signierte Adressen zu sehen, in der Leseansicht wie
 * im PDF. Hochgeladen wird über die cloud-api (`/api/media`, Ziel
 * PRIVATE_MEDIA_API), gebunden und
 * signiert über content-api.
 */
import { getAccessToken } from '../auth'
import { CONTENT_API, PRIVATE_MEDIA_API, STORAGE_API } from '../config'
import { uploadFile } from './storage'

const PREF_KEY = 'content_upload_private'

/** Storage-Medien-URL mit ID und optionaler Query. */
export const STORAGE_MEDIA_RE = STORAGE_API
  ? new RegExp(`^${STORAGE_API.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\/$/, '')}/storage/media/(\\d+)(\\?.*)?$`)
  : null

export function storageMediaUrl(id: number | string): string {
  return `${STORAGE_API.replace(/\/$/, '')}/storage/media/${id}`
}

function authHeaders(json = false): Record<string, string> {
  const token = getAccessToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

async function errorText(res: Response): Promise<string> {
  try {
    const data = await res.json()
    const detail = data?.detail
    return typeof detail === 'string' ? detail : detail?.message || detail?.error || `HTTP ${res.status}`
  } catch {
    return `HTTP ${res.status}`
  }
}

/** Sind private Bilder an diesem Beitrag eingerichtet? */
export async function privateMediaAvailable(postId: number): Promise<boolean> {
  if (!PRIVATE_MEDIA_API || !STORAGE_API || !getAccessToken()) return false
  try {
    const res = await fetch(`${CONTENT_API}/api/v1/posts/${postId}/media-capabilities`, { headers: authHeaders() })
    if (!res.ok) return false
    return (await res.json()).private_media === true
  } catch {
    return false
  }
}

export function privateUploadPreferred(): boolean {
  try { return localStorage.getItem(PREF_KEY) === '1' } catch { return false }
}

export function setPrivateUploadPreferred(on: boolean): void {
  try { localStorage.setItem(PREF_KEY, on ? '1' : '0') } catch { /* privat */ }
}

/** Lädt privat hoch und bindet das Bild an den Beitrag. */
async function uploadPrivate(file: File, postId: number): Promise<{ url: string }> {
  const form = new FormData()
  form.append('file', file)
  const up = await fetch(`${PRIVATE_MEDIA_API}/api/media`, { method: 'POST', headers: authHeaders(), body: form })
  if (!up.ok) throw new Error(`Privater Upload: ${await errorText(up)}`)
  const item = (await up.json()) as { id?: number }
  if (typeof item.id !== 'number') throw new Error('Privater Upload: keine ID erhalten')
  const bind = await fetch(`${CONTENT_API}/api/v1/posts/${postId}/media-grants`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ storage_id: item.id }),
  })
  if (!bind.ok) {
    // Ohne Bindung sähe niemand außer dem Eigentümer das Bild. Lieber laut.
    throw new Error(`Bild hochgeladen, aber nicht an den Beitrag gebunden: ${await errorText(bind)}`)
  }
  return { url: storageMediaUrl(item.id) }
}

/**
 * Upload für ein Bild oder eine Datei, die in einen Beitrag eingefügt wird.
 * `isPrivate` entscheidet der Aufrufer (Schalter im Editor); sonst der
 * bisherige öffentliche Weg.
 */
export async function uploadForPost(
  file: File,
  opts: { postId: number; context: string; isPrivate: boolean },
): Promise<{ url: string }> {
  if (opts.isPrivate) return uploadPrivate(file, opts.postId)
  const { url } = await uploadFile(file, { isPublic: true, permanent: true, context: opts.context })
  return { url }
}

/** Signierte Adressen für private Bilder dieses Beitrags (nur gebundene). */
export async function signedMediaUrls(postId: number, ids: number[]): Promise<{ urls: Record<string, string>; ttl: number }> {
  const res = await fetch(`${CONTENT_API}/api/v1/posts/${postId}/media-urls`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ ids }),
  })
  if (!res.ok) return { urls: {}, ttl: 0 }
  return res.json()
}
